import React from 'react'
import { Alert } from 'react-bootstrap'
import {
  BooleanRadioButtonField, DateField, IField, MultiLineField, MultiSelectField, validateFieldsEx, yesNoDataSource,
} from '../../../../../shared/fields'
import { ApplicationModel } from '../../../../../models/ApplicationModel'
import { Option } from '../../../../../shared/fields/Options'
import {
  getMarketCountries, getLastFY, getThisFY, getMostRecentExportStart,
} from '../../../../../shared/Utility'

import { ExportMarketFinancials } from '../otherTables/ExportMarketFinancials'
import { ISupportSpinner } from '../../../../../shared/Ajax'
import { ApplicationController } from '../../../../../controllers/ApplicationController'
import EMDGAccordion from '../../../../../shared/EMDGAccordion'
import { GRANT_GUIDELINES_URL } from '../../..'

export interface Props {
    model: ApplicationModel
    onChange: { (changes: Partial<ApplicationModel>, callback?: { (): any }): any }
    isSelected: boolean
    tierSelected: boolean
    markets: Option<string>[]
    marketsRegions: string[]
    preview?: boolean
}

export interface State {
    loading: boolean
    filteredMarkets: Option<string>[],
    lastFYStr : string,
    lastFYGuid: string,
    thisFYStr: string,
    thisFYGuid: string,
}

export default class Tier2 extends React.Component<Props, State> implements IField, ISupportSpinner {
  ajax: ApplicationController
  hasEverExported: BooleanRadioButtonField
  dateOfMostRecentExport: DateField
  expenseAreExpandingExportPromotion: BooleanRadioButtonField
  earningsTableWidget: ExportMarketFinancials
  hasDesignatedConnectionToEligibleProducts: BooleanRadioButtonField
  designatedConnectionToEligibleProducts: MultiLineField

  constructor(props: Props) {
    super(props)

    const { markets, marketsRegions } = this.props

    const filteredMarkets = getMarketCountries(markets, marketsRegions)

    this.ajax = new ApplicationController()
    const currDate = new Date()

    this.state = {
      loading: true,
      filteredMarkets,
      lastFYStr: getLastFY(currDate), // e.g. "2021-22",
      lastFYGuid: '',
      thisFYStr: getThisFY(currDate),
      thisFYGuid: '',
    }
  }

  componentDidMount() {
    const { lastFYStr, thisFYStr } = this.state

    const lastFYName = lastFYStr.substring(0, 4) // e.g. 2021
    const thisFYName = thisFYStr.substring(0, 4)
    this.ajax.getFinYearsGuid(
      this,
      lastFYName,
      thisFYName,
      (resultArr) => {
        /* resultArr
                    label: "2021"
                    value: "d04c8def-4a90-eb11-b1ac-0123456789012"
                */
        const lastFYObj = resultArr.find((item) => item.label == lastFYName)
        const thisFYObj = resultArr.find((item) => item.label == thisFYName)
        // store values, ready to use depending on Boolean Y/N Q42.d)
        this.setState({
          lastFYGuid: lastFYObj.value,
          thisFYGuid: thisFYObj.value,
        }, () => { this.exportIncomeFYString(true) })
      },
      (statusTxt, errThrown) => {
        console.error('Tier2.tsx::getFinYearsGuid-ERR:errThrown:', errThrown)
      },
    )
  }

  componentDidUpdate(prevProps: Props) {
    const { onChange, model, isSelected } = this.props

    if (isSelected && model.hasEverExported !== prevProps.model.hasEverExported) {
      onChange({
        dateOfMostRecentExport: null,
        expenseAreExpandingExportPromotion: null,
        hasDesignatedConnectionToEligibleProducts: null,
        designatedConnectionToEligibleProducts: null,
      })
    }

    if (isSelected && model.dateOfMostRecentExport !== prevProps.model.dateOfMostRecentExport) {
      onChange({
        expenseAreExpandingExportPromotion: null,
        hasDesignatedConnectionToEligibleProducts: null,
        designatedConnectionToEligibleProducts: null,
      })
    }
  }

  validate(callback: { (res: boolean): any }, focus?: boolean) {
    // while there are RED alert boxes...
    const { model } = this.props
    if (model.hasEverExported === false
        || model.expenseAreExpandingExportPromotion === false
      || model.hasDesignatedConnectionToEligibleProducts === false) {
      callback && callback(false) // don't ever continue!
      return
    }
    validateFieldsEx(
      focus,
      callback,
      this.hasEverExported,
      this.dateOfMostRecentExport,
      this.expenseAreExpandingExportPromotion,
      this.earningsTableWidget,
      this.hasDesignatedConnectionToEligibleProducts,
      this.designatedConnectionToEligibleProducts,
    )
  }

  toggleSpinner(on: boolean, callback: { (): any }): void {
    this.setState({ loading: on }, callback)
  }

  // Q42.d (Y/N) determines Q42.e (the question above the exportMarketFinancials table)
  exportIncomeFYString = (forLastFinYearYN: boolean) => {
    const { onChange } = this.props

    // if Y: Q42.e is for lastFY, if N: Q42.e is for thisFY
    const finYearGuid = forLastFinYearYN ? this.state.lastFYGuid : this.state.thisFYGuid
    onChange({
      isExportIncomeForLastFY: forLastFinYearYN,
      financialYearOfExportEarnings: finYearGuid || null,
    })
  }

  isActivelyExporting = (mostRecentExport:string): boolean => {
    if (mostRecentExport != null) {
      const allowedMostRecentExportDate = getMostRecentExportStart(18)
      return new Date(mostRecentExport) >= allowedMostRecentExportDate
    }
    return true
  }

  render() {
    const {
      model, onChange, isSelected, tierSelected, markets, marketsRegions, preview,
    } = this.props
    const { lastFYStr } = this.state

    const labelHasEverExportedTier2 = '32. Are you exporting eligible products?'
    const labelDateOfMostRecentExport = '32.a Provide the month and year of your most recent export.'
    const labelExpenseAreExpandingExportPromotion = '33. Are you seeking to expand your export promotional activity for eligible products within your existing export markets?'
    const labelHasDesignatedConnectionToEligibleProducts = '34. Do you have a designated connection to eligible products?'
    const labelDesignatedConnectionToEligibleProducts = '34.a Explain how you have a designated connection to your eligible products?'
    // NB: This`skeleton` section appears if Tiers radio button is not selected; this only happens on New Application;
    const skeleton = (
      <>

        <DateField
          id="dateOfMostRecentExportSkeletonTier2"
          label={labelDateOfMostRecentExport}
          value={null}
          onChange={() => { }}
          disabled
        />
        <BooleanRadioButtonField
          id="expenseAreExpandingExportPromotionSkeletonTier2"
          label={labelExpenseAreExpandingExportPromotion}
          value={null}
          onChange={() => { }}
          dataSource={yesNoDataSource}
          disabled
        />

      </>

    )

    const formElems = (
      <>
        {model.hasEverExported === false && (
        <Alert className="emdg-page-alerts emdg-page-alerts--error">
          You are
          {' '}
          <b>not</b>
          {' '}
          eligible to apply for Tier 2.
        </Alert>
        )}

        {model.hasEverExported && (
        <>
          <Alert className="emdg-page-alerts">
            You must provide at least 2 export sale invoices of your eligible product(s) issued over the course of the
            {' '}
            {lastFYStr}
            {' '}
            financial year.
            {' '}
            One of the invoices must be within 18 months of the application.
          </Alert>
          <DateField
            ref={(r) => { this.dateOfMostRecentExport = r }}
            id="dateOfMostRecentExport"
            label={labelDateOfMostRecentExport}
            value={isSelected ? model.dateOfMostRecentExport : null}
            onChange={(dateOfMostRecentExport) => onChange({ dateOfMostRecentExport })}
            required={model.hasEverExported}
            disabled={!isSelected}
            monthOnly
            preview={preview}
          />
        </>
        )}

        {model.dateOfMostRecentExport && !this.isActivelyExporting(model.dateOfMostRecentExport)
            && (
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              You are
              {' '}
              <b>not</b>
              {' '}
              eligible for Tier 2 as you are not currently exporting.
            </Alert>
            )}

        {model.dateOfMostRecentExport && this.isActivelyExporting(model.dateOfMostRecentExport) && (
        <>
          <ExportMarketFinancials
            ref={(r) => { this.earningsTableWidget = r }}
            model={model}
            markets={markets}
            marketsRegions={marketsRegions}
            onChange={onChange}
            tierNo={2}
            preview={preview}
          />

          <BooleanRadioButtonField
            ref={(r) => {
              this.expenseAreExpandingExportPromotion = r
            }}
            id="expenseAreExpandingExportPromotion"
            label={labelExpenseAreExpandingExportPromotion}
            value={
                  isSelected ? model.expenseAreExpandingExportPromotion : null
                }
            onChange={(expenseAreExpandingExportPromotion) => onChange({ expenseAreExpandingExportPromotion })}
            required={!!model.dateOfMostRecentExport}
            dataSource={yesNoDataSource}
            disabled={!isSelected}
            preview={preview}
          />

          {model.expenseAreExpandingExportPromotion && (
          <Alert className="emdg-page-alerts">
            Provide further details in the Plan to market section.
          </Alert>
          )}

          {model.expenseAreExpandingExportPromotion === false && (
          <Alert className="emdg-page-alerts emdg-page-alerts--error">
            You are
            {' '}
            <b>not</b>
            {' '}
            eligible for Tier 2 as you are not seeking to
            expand your export promotional activity.
          </Alert>
          )}

          <BooleanRadioButtonField
            ref={(r) => { this.hasDesignatedConnectionToEligibleProducts = r }}
            id="hasDesignatedConnectionToEligibleProducts"
            label={labelHasDesignatedConnectionToEligibleProducts}
            value={isSelected ? model.hasDesignatedConnectionToEligibleProducts : null}
            onChange={(hasDesignatedConnectionToEligibleProducts) => onChange({ hasDesignatedConnectionToEligibleProducts })}
            required={isSelected}
            dataSource={yesNoDataSource}
            disabled={!isSelected}
            preview={preview}
            description={(
              <i>
                You must have a designated connection to the eligible product for your promotional activity expenses to be eligible. Refer to section 5.3.3 of the
                {' '}
                <a target="blank" href={`${GRANT_GUIDELINES_URL}`} rel="noreferrer">Grant Guidelines</a>
                {' '}
                for further information.
              </i>
              )}
          />

          {model.hasDesignatedConnectionToEligibleProducts === false && (
          <Alert className="emdg-page-alerts emdg-page-alerts--error">
            You are
            {' '}
            <b>not</b>
            {' '}
            eligible to apply for Tier 2.
          </Alert>
          )}

          {model.hasDesignatedConnectionToEligibleProducts && (
          <MultiLineField
            ref={(r) => { this.designatedConnectionToEligibleProducts = r }}
            id="designatedConnectionToEligibleProducts"
            label={labelDesignatedConnectionToEligibleProducts}
            value={model.designatedConnectionToEligibleProducts}
            onChange={(designatedConnectionToEligibleProducts) => onChange({ designatedConnectionToEligibleProducts })}
            required
            maxLength={1000}
            preview={preview}
          />
          )}

        </>
        )}
      </>
    )

    return (
      <div className="form-item">
        <div>
          Tier 2 applicants must be seeking EMDG funding to assist them in growing their export promotional activity for eligible products within your existing export markets.
          {' '}
          If you are not seeking to grow your export promotional activity within your existing export markets, you are not eligible to apply for this tier.
        </div>
        <EMDGAccordion expanded header="Tier 2 agreement">
          <BooleanRadioButtonField
            ref={(r) => { this.hasEverExported = r }}
            id="hasEverExportedTier2"
            label={labelHasEverExportedTier2}
            value={isSelected ? model.hasEverExported : null}
            onChange={(hasEverExported) => onChange({ hasEverExported })}
            required={isSelected}
            dataSource={yesNoDataSource}
            disabled={!isSelected}
            preview={preview}
          />
          { tierSelected ? formElems : skeleton}
        </EMDGAccordion>
      </div>
    )
  }
}
